import DayPickerInput from 'react-day-picker/DayPickerInput';
import PropTypes from '@root/vendor/prop-types';
import React, { useState } from '@root/vendor/react';
import Responsive from '@root/core/src/utils/responsive';
import calendar from '@root/core/src/assets/calendar.svg';
import dayjs from '@root/vendor/dayjs';
import { Colors, StyleSheet, Theme, createEmotionClassName } from '@root/core/src/utils/styles';
import { dayPickerInputStyles, dayPickerStyles } from '@root/core/src/utils/datepicker-styles';

export default function Datepicker({
  dateLimit = null, inputStyles = dayPickerInputStyles, pickerStyles = dayPickerStyles, disablePastDays = false, errorLabel, isError = false, label,
  onDateChange, onDayPickerHide, onDayPickerShow, today = new Date(), ...props
}) {
  const [selectedDay, setSelectedDay] = useState(props.selectedDay);

  const weekdaysShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const dateFormat = 'MMM DD, YYYY';

  const inputId = props.inputId || label.toLowerCase().replace(' ', '-') + '-input';
  const labelId = inputId + '-label';
  const iconLabelId = inputId + '-icon-label';

  const selectedDayIsValid = (past, outside) => (!disablePastDays || !past) && (!dateLimit || !outside);

  const selectDay = (day, { past, outside }) => {
    if (selectedDayIsValid(past, outside)) {
      setSelectedDay(day);
      onDateChange(day);
    }
  };

  const formatDate = (date) => {
    let dateToFormat = selectedDay;
    let format = 'M/D/YY';

    if ((!disablePastDays || date >= today) && (!dateLimit || date <= dateLimit)) {
      dateToFormat = date;
    }

    if (Responsive.matches(Responsive.QUERIES.sm)) {
      format = 'MMM D, YYYY';
    }

    return dayjs(dateToFormat).format(format);
  };

  const containerStyles = [inputStyles.container];
  if (isError || errorLabel) {
    containerStyles.push(inputStyles.errorContainer);
  }

  const dayPickerInputClassNames = {
    container: createEmotionClassName(containerStyles),
    overlayWrapper: createEmotionClassName(inputStyles.overlayWrapper),
    overlay: createEmotionClassName(inputStyles.overlay),
  };

  const dayPickerClassNames = {
    container: createEmotionClassName(pickerStyles.container),
    wrapper: createEmotionClassName(pickerStyles.wrapper),
    interactionDisabled: createEmotionClassName(pickerStyles.interactionDisabled),
    navBar: createEmotionClassName(pickerStyles.navBar),
    navButtonPrev: createEmotionClassName(pickerStyles.navButtonPrev),
    navButtonNext: createEmotionClassName(pickerStyles.navButtonNext),
    navButtonInteractionDisabled: createEmotionClassName(pickerStyles.navButtonInteractionDisabled),
    months: createEmotionClassName(pickerStyles.months),
    month: createEmotionClassName(pickerStyles.month),
    caption: createEmotionClassName(pickerStyles.caption),
    weekdays: createEmotionClassName(pickerStyles.weekdays),
    weekdaysRow: createEmotionClassName(pickerStyles.weekdaysRow),
    weekday: createEmotionClassName(pickerStyles.weekday),
    weekNumber: createEmotionClassName(pickerStyles.weekday),
    body: createEmotionClassName(pickerStyles.body),
    week: createEmotionClassName(pickerStyles.week),
    day: createEmotionClassName(pickerStyles.day),
    footer: createEmotionClassName(pickerStyles.footer),
    todayButton: createEmotionClassName(pickerStyles.todayButton),
    today: '',
    selected: createEmotionClassName(pickerStyles.selected),
    disabled: createEmotionClassName(pickerStyles.disabled),
    outside: createEmotionClassName(pickerStyles.outside),
    past: createEmotionClassName(pickerStyles.past),
  };

  const dayPickerProps = {
    onDayClick: selectDay,
    selectedDays: selectedDay,
    weekdaysShort,
    modifiers: {
      today,
    },
    classNames: dayPickerClassNames,
  };

  if (disablePastDays) {
    dayPickerProps.fromMonth = today;
    dayPickerProps.modifiers.past = {
      before: today,
    };
  }

  if (dateLimit) {
    dayPickerProps.toMonth = dateLimit;
    dayPickerProps.modifiers.outside = {
      after: dateLimit,
    };
  }

  const inputProps = {
    readOnly: true,
    'data-isdaypickerinput': true,
    id: inputId,
  };

  const labelStyles = [styles.labelFull,
    isError || errorLabel ? styles.errorLabel : '',
  ];

  return (
    <div css={styles.wrapper}>
      <DayPickerInput
        classNames={dayPickerInputClassNames}
        dayPickerProps={dayPickerProps}
        format={dateFormat}
        formatDate={formatDate}
        hideOnDayClick={true}
        inputProps={inputProps}
        keepFocus={true}
        onDayPickerHide={onDayPickerHide}
        onDayPickerShow={onDayPickerShow}
        placeholder={'Choose date'}
        value={selectedDay || ''}
      />
      <label
        css={labelStyles}
        htmlFor={inputId}
        id={labelId}
      >
        {errorLabel || label}
      </label>
      <label
        css={[styles.labelFull, styles.labelRight, inputStyles.iconLabel]}
        htmlFor={inputId}
        id={iconLabelId}
      >
        <img src={calendar} />
      </label>
    </div>
  );
}

Datepicker.propTypes = {
  dateLimit: PropTypes.instanceOf(Date),
  disablePastDays: PropTypes.bool,
  errorLabel: PropTypes.string,
  inputId: PropTypes.string.isRequired,
  inputStyles: PropTypes.object,
  isError: PropTypes.bool,
  isSmall: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onDateChange: PropTypes.func.isRequired,
  onDayPickerHide: PropTypes.func,
  onDayPickerShow: PropTypes.func,
  pickerStyles: PropTypes.object,
  selectedDay: PropTypes.instanceOf(Date),
  today: PropTypes.instanceOf(Date),
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative',
  },
  labelFull: {
    ...Theme.paragraph1(),
    pointerEvents: 'none',
    paddingLeft: '15px',
    position: 'absolute',
    top: 17,
    left: 0,
    display: 'flex',
    alignItems: 'center',
  },
  labelRight: {
    right: 0,
    left: 'auto',
    paddingRight: '15px',
  },
  errorLabel: {
    color: Colors.error(),
  },
});
